import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSettingsStore } from "@/store/api/settingsStore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { ScheduleTimePicker, SCHEDULE_TIME_PATTERN } from "./ScheduleTimePicker";

const ALLOWED_SLOT_DURATIONS = [15, 30, 45, 60, 90];

const BOOKING_ADVANCE_OPTIONS = [
  { value: 7, label: "7" },
  { value: 14, label: "14" },
  { value: 30, label: "30" },
  { value: 60, label: "60" },
  { value: 90, label: "90" },
  { value: 0, label: "unlimited" }
];

// Ordre d'affichage : Lundi -> Dimanche, avec la valeur getDay() correspondante (0=Dim, 1=Lun, ...)
const WEEK_DAYS: { value: number; dayKey: string }[] = [
  { value: 1, dayKey: "monday" },
  { value: 2, dayKey: "tuesday" },
  { value: 3, dayKey: "wednesday" },
  { value: 4, dayKey: "thursday" },
  { value: 5, dayKey: "friday" },
  { value: 6, dayKey: "saturday" },
  { value: 0, dayKey: "sunday" },
];

const DEFAULT_OPEN_DAYS = [1, 2, 3, 4, 5, 6];

export function ScheduleTab() {
  const { t } = useTranslation(["settings", "bookingui"]);
  const { settings, updateSettings, isLoading } = useSettingsStore();
  const [slotDuration, setSlotDuration] = useState(settings.schedule?.slotDuration || 45);
  const [startTime, setStartTime] = useState(settings.schedule?.startTime || "08:00");
  const [endTime, setEndTime] = useState(settings.schedule?.endTime || "21:00");
  // `?? 30` et non `|| 30` : 0 est une valeur légitime (« illimité »), que
  // `||` remplaçait silencieusement par 30 au rechargement, puis réécrivait.
  const [bookingAdvanceDays, setBookingAdvanceDays] = useState(settings.schedule?.bookingAdvanceDays ?? 30);
  const [defaultBypassCooldown, setDefaultBypassCooldown] = useState(settings.schedule?.defaultBypassCooldown || false);
  const [hideAvailableSlotsForClients, setHideAvailableSlotsForClients] = useState(settings.schedule?.hideAvailableSlotsForClients || false);
  const [openDaysOfWeek, setOpenDaysOfWeek] = useState<number[]>(
    settings.schedule?.openDaysOfWeek ?? DEFAULT_OPEN_DAYS
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSlotDuration(settings.schedule?.slotDuration || 45);
    setStartTime(settings.schedule?.startTime || "08:00");
    setEndTime(settings.schedule?.endTime || "21:00");
    setBookingAdvanceDays(settings.schedule?.bookingAdvanceDays ?? 30);
    setDefaultBypassCooldown(settings.schedule?.defaultBypassCooldown || false);
    setHideAvailableSlotsForClients(settings.schedule?.hideAvailableSlotsForClients || false);
    setOpenDaysOfWeek(settings.schedule?.openDaysOfWeek ?? DEFAULT_OPEN_DAYS);
  }, [settings.schedule]);

  const toggleDay = (day: number, checked: boolean) => {
    setOpenDaysOfWeek((prev) => {
      const set = new Set(prev);
      if (checked) set.add(day);
      else set.delete(day);
      return Array.from(set).sort((a, b) => a - b);
    });
  };

  const handleSave = async () => {
    if (!ALLOWED_SLOT_DURATIONS.includes(slotDuration)) {
      toast.error(t("schedule.invalidDuration"));
      return;
    }

    if (!SCHEDULE_TIME_PATTERN.test(startTime) || !SCHEDULE_TIME_PATTERN.test(endTime) || startTime >= endTime) {
      toast.error(t("schedule.invalidTimeRange"));
      return;
    }

    if (openDaysOfWeek.length === 0) {
      toast.error(t("scheduleTab.atLeastOneDayOpen", { ns: "bookingui" }));
      return;
    }

    const confirmed = window.confirm(t("schedule.confirmReload"));
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await updateSettings({
        schedule: {
          slotDuration: slotDuration,
          startTime: startTime,
          endTime: endTime,
          bookingAdvanceDays: bookingAdvanceDays,
          defaultBypassCooldown: defaultBypassCooldown,
          hideAvailableSlotsForClients: hideAvailableSlotsForClients,
          openDaysOfWeek: openDaysOfWeek,
        },
      });

      toast.success(t("schedule.saveSuccess"));

      // Rafraîchir pour propager les changements
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      toast.error(t("schedule.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const currentOpenDays = settings.schedule?.openDaysOfWeek ?? DEFAULT_OPEN_DAYS;
  const daysChanged =
    openDaysOfWeek.length !== currentOpenDays.length ||
    openDaysOfWeek.some((d) => !currentOpenDays.includes(d));

  const hasChanges =
    slotDuration !== (settings.schedule?.slotDuration || 45) ||
    startTime !== (settings.schedule?.startTime || "08:00") ||
    endTime !== (settings.schedule?.endTime || "21:00") ||
    bookingAdvanceDays !== (settings.schedule?.bookingAdvanceDays ?? 30) ||
    defaultBypassCooldown !== (settings.schedule?.defaultBypassCooldown || false) ||
    hideAvailableSlotsForClients !== (settings.schedule?.hideAvailableSlotsForClients || false) ||
    daysChanged;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("schedule.title")}</CardTitle>
        <CardDescription>{t("schedule.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pb-8">
        {/* Alerte d'information */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {t("schedule.slotDurationWarning")}
          </AlertDescription>
        </Alert>

        {/* Sélecteur de durée */}
        <div className="space-y-2">
          <Label htmlFor="slot-duration">{t("schedule.slotDurationLabel")}</Label>
          <Select
            value={slotDuration.toString()}
            onValueChange={(val) => setSlotDuration(parseInt(val))}
            disabled={isLoading || isSaving}
          >
            <SelectTrigger id="slot-duration" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15 {t("schedule.minutes")}</SelectItem>
              <SelectItem value="30">30 {t("schedule.minutes")}</SelectItem>
              <SelectItem value="45">45 {t("schedule.minutes")} ({t("schedule.default")})</SelectItem>
              <SelectItem value="60">60 {t("schedule.minutes")}</SelectItem>
              <SelectItem value="90">90 {t("schedule.minutes")}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t("schedule.slotDurationHelp")}
          </p>
        </div>

        {/* Heure de début */}
        <div className="space-y-2">
          <Label htmlFor="start-time">{t("schedule.startTimeLabel")}</Label>
          <ScheduleTimePicker id="start-time" value={startTime} onChange={setStartTime} disabled={isLoading || isSaving} />
        </div>

        {/* Heure de fin */}
        <div className="space-y-2">
          <Label htmlFor="end-time">{t("schedule.endTimeLabel")}</Label>
          <ScheduleTimePicker id="end-time" value={endTime} onChange={setEndTime} disabled={isLoading || isSaving} />
        </div>

        {/* Limite de réservation à l'avance */}
        <div className="space-y-2">
          <Label htmlFor="booking-advance">{t("schedule.bookingAdvanceDaysLabel")}</Label>
          <Select
            value={bookingAdvanceDays.toString()}
            onValueChange={(val) => setBookingAdvanceDays(parseInt(val))}
            disabled={isLoading || isSaving}
          >
            <SelectTrigger id="booking-advance" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BOOKING_ADVANCE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.value === 0
                    ? t("schedule.bookingAdvanceDaysUnlimited")
                    : `${option.label} ${t("schedule.days")}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t("schedule.bookingAdvanceDaysHelp")}
          </p>
        </div>

        {/* Jours ouverts aux réservations */}
        <div className="space-y-2 pt-4 border-t">
          <Label>{t("scheduleTab.openDaysLabel", { ns: "bookingui" })}</Label>
          <p className="text-sm text-muted-foreground">
            {t("scheduleTab.openDaysDescription", { ns: "bookingui" })}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
            {WEEK_DAYS.map((day) => {
              const id = `open-day-${day.value}`;
              return (
                <div key={day.value} className="flex items-center justify-between rounded-md border p-3">
                  <Label htmlFor={id} className="cursor-pointer">
                    {t(`days.${day.dayKey}`, { ns: "bookingui" })}
                  </Label>
                  <Switch
                    id={id}
                    checked={openDaysOfWeek.includes(day.value)}
                    onCheckedChange={(checked) => toggleDay(day.value, checked)}
                    disabled={isLoading || isSaving}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Règle des 48h par défaut */}
        <div className="space-y-2 pt-4 border-t">
          <div className="flex items-center space-x-3">
            <Switch
              id="default-bypass-cooldown"
              checked={defaultBypassCooldown}
              onCheckedChange={setDefaultBypassCooldown}
              disabled={isLoading || isSaving}
            />
            <Label htmlFor="default-bypass-cooldown" className="cursor-pointer">
              {t("schedule.defaultBypassCooldownLabel")}
            </Label>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("schedule.defaultBypassCooldownHelp")}
          </p>
        </div>

        {/* Masquer les places disponibles pour les adhérents */}
        <div className="space-y-2 pt-4 border-t">
          <div className="flex items-center space-x-3">
            <Switch
              id="hide-available-slots"
              checked={hideAvailableSlotsForClients}
              onCheckedChange={setHideAvailableSlotsForClients}
              disabled={isLoading || isSaving}
            />
            <Label htmlFor="hide-available-slots" className="cursor-pointer">
              {t("schedule.hideAvailableSlotsLabel")}
            </Label>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("schedule.hideAvailableSlotsHelp")}
          </p>
        </div>

        {/* Bouton de sauvegarde */}
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isLoading || isSaving}
          className="w-full sm:w-auto"
        >
          {isSaving ? t("common:saving") : t("common:save")}
        </Button>
      </CardContent>
    </Card>
  );
}
